import { gsap } from 'gsap';

/**
 * Transitions entre les pages (site multi-pages : chaque lien recharge la page).
 *
 * Trois types, choisis automatiquement selon la destination :
 *  - 'enter' : depuis le hub, le bouton cliqué s'agrandit en cadre néon (RVB décalé)
 *              et "avale" l'écran. La nouvelle page se révèle par bandes glitch.
 *  - 'exit'  : vers le hub, l'écran s'éteint comme un vieux téléviseur (ligne néon
 *              qui se resserre). Le hub se rallume ensuite, comme une enseigne.
 *  - 'swipe' : entre les autres pages, bandes glitch qui couvrent puis révèlent.
 *
 * Fonctionnement : animation de SORTIE -> on note le type dans sessionStorage ->
 * navigation -> la nouvelle page lit la note et joue l'animation d'ARRIVÉE.
 */
export default class PageTransition {
  constructor(element) {
    this.element = element;

    this.options = {
      storageKey: 'pagetransition',
      maxAge: 5000, // ms : au-delà, la note est ignorée
      loadTimeout: 1200, // ms : on ne fait pas attendre l'arrivée plus longtemps
      stripCount: 8,
      portalRadius: 14, // px : rayon de départ du cadre (≈ rayon de tes boutons)
      // Hôtes considérés comme "ton site" même si l'origine diffère (ex.: lien Hub du header)
      internalHosts: ['guillaumecallebert.dev', 'www.guillaumecallebert.dev'],
    };

    this.isBusy = false;
    this.timeline = null;
    this.html = document.documentElement;
    this.reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    this.onClick = this.onClick.bind(this);
    this.onPageShow = this.onPageShow.bind(this);

    this.build();
    this.element.addEventListener('click', this.onClick);
    // Bouton "précédent" : la page revient du cache avec l'overlay encore affiché
    window.addEventListener('pageshow', this.onPageShow);

    this.playArrival();
  }

  /* ---------- Construction ---------- */

  build() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'pagetransition';
    this.overlay.setAttribute('aria-hidden', 'true');
    this.overlay.innerHTML = `
      <div class="pt-strips"></div>
      <div class="pt-portal">
        <div class="pt-frame pt-frame--magenta"></div>
        <div class="pt-frame pt-frame--cyan"></div>
        <div class="pt-frame pt-frame--main"></div>
      </div>
      <div class="pt-crt">
        <div class="pt-bar pt-bar--top"></div>
        <div class="pt-bar pt-bar--bottom"></div>
        <div class="pt-line"></div>
      </div>
    `;

    this.stripsGroup = this.overlay.querySelector('.pt-strips');
    this.portal = this.overlay.querySelector('.pt-portal');
    this.frames = [...this.overlay.querySelectorAll('.pt-frame')]; // magenta, cyan, main
    this.crt = this.overlay.querySelector('.pt-crt');
    this.bars = [...this.overlay.querySelectorAll('.pt-bar')];
    this.line = this.overlay.querySelector('.pt-line');

    const { stripCount } = this.options;
    const height = 100 / stripCount;
    this.strips = [];
    for (let i = 0; i < stripCount; i++) {
      const strip = document.createElement('div');
      strip.className = 'pt-strip';
      strip.style.top = `${i * height}%`;
      strip.style.height = `calc(${height}% + 1px)`; // +1px : pas de fente entre les bandes
      this.stripsGroup.appendChild(strip);
      this.strips.push(strip);
    }

    document.body.appendChild(this.overlay);
    this.hideAll();
  }

  hideAll() {
    gsap.set([this.stripsGroup, this.portal, this.crt], { autoAlpha: 0 });
    this.overlay.classList.remove('is-active', 'is-blocking');
  }

  activate(group, blocking = false) {
    this.overlay.classList.add('is-active');
    this.overlay.classList.toggle('is-blocking', blocking);
    gsap.set(group, { autoAlpha: 1 });
  }

  /* ---------- Détection des liens ---------- */

  cleanPath(path) {
    return (
      path
        .replace(/index\.html$/, '')
        .replace(/\.html$/, '')
        .replace(/\/$/, '') || '/'
    );
  }

  isInternal(url) {
    return (
      url.origin === location.origin ||
      this.options.internalHosts.includes(url.hostname)
    );
  }

  isHubPath(path) {
    return this.cleanPath(path) === '/';
  }

  onClick(event) {
    if (this.isBusy || event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;

    const link = event.target.closest('a[href]');
    if (!link) return;
    if (link.target && link.target !== '_self') return;
    if (link.hasAttribute('download')) return;

    const url = new URL(link.href, location.href);
    if (!/^https?:$/.test(url.protocol)) return; // mailto:, tel:, sms:...
    if (!this.isInternal(url)) return;
    // Même page (ancre, etc.) : c'est le travail de SmoothScroller
    if (this.cleanPath(url.pathname) === this.cleanPath(location.pathname))
      return;
    if (this.reducedMotion) return;

    event.preventDefault();

    let type = 'swipe';
    if (this.isHubPath(url.pathname)) type = 'exit';
    else if (this.isHubPath(location.pathname)) type = 'enter';

    this.leave(type, url.href, link.getBoundingClientRect());
  }

  /* ---------- SORTIE ---------- */

  leave(type, href, rect) {
    this.isBusy = true;

    const go = () => {
      try {
        sessionStorage.setItem(
          this.options.storageKey,
          JSON.stringify({ type, time: Date.now() }),
        );
      } catch (e) {
        /* stockage indisponible : la page s'ouvrira sans animation d'arrivée */
      }
      window.location.assign(href);
    };

    // Filet de sécurité : si la navigation n'a pas lieu, on libère la page
    this.failsafe = setTimeout(() => this.reset(), 5000);

    if (type === 'enter') this.leaveWithPortal(rect, go);
    else if (type === 'exit') this.leaveWithCrt(go);
    else this.leaveWithStrips(go);
  }

  // Le bouton devient un cadre néon qui s'agrandit jusqu'à couvrir l'écran.
  // Magenta et cyan partent en avance : leurs bords dépassent du cadre principal (effet RVB).
  leaveWithPortal(rect, done) {
    const w = this.overlay.clientWidth;
    const h = this.overlay.clientHeight;
    this.activate(this.portal, true);

    this.frames.forEach((frame) => {
      gsap.set(frame, {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        borderRadius: this.options.portalRadius,
      });
    });

    this.timeline = gsap.timeline({ onComplete: done });
    this.frames.forEach((frame, i) => {
      this.timeline.to(
        frame,
        {
          left: 0,
          top: 0,
          width: w,
          height: h,
          borderRadius: 0,
          duration: 0.55,
          ease: 'power3.in',
        },
        i * 0.05,
      );
    });
  }

  // Extinction façon vieux téléviseur : deux volets se ferment, il reste une ligne qui se resserre.
  leaveWithCrt(done) {
    this.activate(this.crt, true);
    gsap.set(this.bars, { height: 0 });
    gsap.set(this.line, { opacity: 0, scaleX: 1, yPercent: -50 });

    this.timeline = gsap
      .timeline({ onComplete: done })
      .to(this.bars, { height: '50.5%', duration: 0.45, ease: 'power3.in' })
      .set(this.line, { opacity: 1 }, '-=0.1')
      .to(this.line, { scaleX: 0, duration: 0.25, ease: 'power2.in' });
  }

  // Bandes qui entrent en alternant gauche / droite
  leaveWithStrips(done) {
    this.activate(this.stripsGroup, true);
    gsap.set(this.strips, { xPercent: (i) => (i % 2 ? 105 : -105), x: 0 });

    this.timeline = gsap.timeline({ onComplete: done }).to(this.strips, {
      xPercent: 0,
      duration: 0.45,
      ease: 'power3.out',
      stagger: { each: 0.04, from: 'random' },
    });
  }

  /* ---------- ARRIVÉE ---------- */

  readStored() {
    try {
      const raw = sessionStorage.getItem(this.options.storageKey);
      sessionStorage.removeItem(this.options.storageKey);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (Date.now() - data.time > this.options.maxAge) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  playArrival() {
    const data = this.readStored();
    if (!data) {
      this.html.classList.remove('pt-cover');
      return;
    }
    if (this.reducedMotion) {
      this.html.classList.remove('pt-cover');
      return;
    }

    // État "couvert" identique à la fin de l'animation de sortie
    if (data.type === 'exit') {
      this.activate(this.crt, true);
      gsap.set(this.bars, { height: '50.5%' });
      gsap.set(this.line, { opacity: 0, scaleX: 0, yPercent: -50 });
    } else {
      this.activate(this.stripsGroup, true);
      gsap.set(this.strips, { xPercent: 0, x: 0 });
    }
    // Le voile CSS posé par le script du <head> peut maintenant disparaître
    this.html.classList.remove('pt-cover');

    this.whenReady().then(() => {
      if (data.type === 'exit') this.arriveWithPowerOn();
      else this.arriveWithStrips();
    });
  }

  // Attend la fin du chargement (ou un délai max) pour ne pas révéler une page à moitié chargée
  whenReady() {
    return new Promise((resolve) => {
      let done = false;
      const go = () => {
        if (done) return;
        done = true;
        setTimeout(resolve, 60);
      };
      if (document.readyState === 'complete') go();
      else window.addEventListener('load', go, { once: true });
      setTimeout(go, this.options.loadTimeout);
    });
  }

  // Révélation : petit tremblement glitch, puis les bandes sortent chacune de leur côté
  arriveWithStrips() {
    this.timeline = gsap
      .timeline({ onComplete: () => this.finish() })
      .to(this.strips, {
        x: () => gsap.utils.random(-22, 22),
        duration: 0.06,
        repeat: 3,
        yoyo: true,
        ease: 'none',
        stagger: { each: 0.01, from: 'random' },
      })
      .set(this.strips, { x: 0 })
      .to(
        this.strips,
        {
          xPercent: (i) => (i % 2 ? 105 : -105),
          duration: 0.5,
          ease: 'power3.inOut',
          stagger: { each: 0.045, from: 'random' },
        },
        '+=0.02',
      );
  }

  // Allumage : la ligne s'étire, puis les volets s'ouvrent depuis le centre
  arriveWithPowerOn() {
    this.html.classList.add('pt-poweron'); // scintillement de l'enseigne (SCSS)
    setTimeout(() => this.html.classList.remove('pt-poweron'), 1400);

    this.timeline = gsap
      .timeline({ onComplete: () => this.finish() })
      .to(this.line, {
        opacity: 1,
        scaleX: 1,
        duration: 0.2,
        ease: 'power2.out',
      })
      .to(
        this.bars,
        { height: 0, duration: 0.55, ease: 'power3.out' },
        '+=0.05',
      )
      .to(this.line, { opacity: 0, duration: 0.25 }, '<0.15');
  }

  /* ---------- Nettoyage ---------- */

  finish() {
    this.hideAll();
    this.isBusy = false;
  }

  reset() {
    clearTimeout(this.failsafe);
    if (this.timeline) this.timeline.kill();
    this.hideAll();
    this.html.classList.remove('pt-cover', 'pt-poweron');
    this.isBusy = false;
  }

  onPageShow(event) {
    if (event.persisted) this.reset();
  }
}
